import { ActionCreator, AjaxDispatcher } from "./ajax-dispatcher";
import { addClass, addEventListenerIfNotExists, disable, enable, getApiUrl, getCurrentLanguageId, hide, removeClass, show } from "./common";
import { DeliveryRegion } from "./delivery-region";
import HttpClient from "./http-client";
import LoadingIndication from "./loading-indication";

const VatIdPattern: RegExp = /^[a-zA-Z0-9]*$/;
const EnterKeyCode: number = 13;
const warningCssClass: string = "vatWarning";
const successCssClass: string = "vatSuccess";

export default class VatValidation {
    private vatValidationContainer: HTMLElement;
    private deliveryRegionElement: HTMLElement;
    private validationWarningElement: HTMLElement;
    private validationButtonElement: HTMLElement;
    private changeVatIdButtonElement: HTMLElement;
    private vatValidationBox: HTMLElement;
    private vatIdInput: HTMLInputElement;
    private vatValidationStatusMessage: HTMLElement;
    private vatValidationWarningContainer: HTMLElement;
    private vatCountry: HTMLSelectElement;
    private countryNotPicked: HTMLElement;

    constructor() {
        this.initElements();
        this.initEventListeners();
    }

    private initElements(): void {
        this.vatValidationContainer = document.querySelector('#vatValidationContainer');
        this.deliveryRegionElement = document.querySelector('[data-component="DeliveryRegion"]');
        this.validationWarningElement = document.querySelector('#inputEmptyWarning');
        this.validationButtonElement = document.querySelector('#vatValidationButton');
        this.changeVatIdButtonElement = document.querySelector('#enableChange');
        this.vatValidationBox = document.querySelector('.vatValidationBox');
        this.vatIdInput = document.querySelector('#vatInput');
        this.vatValidationStatusMessage = document.querySelector('#vat-validation-status-message');
        this.vatValidationWarningContainer = document.querySelector('#vatValidationBlockedWarning');
        this.vatCountry = document.querySelector('#vatCountry');
        this.countryNotPicked = document.querySelector('#countryEmptyWarning');
    }

    private initEventListeners(): void {
        this.registerValidationButtonListener();
        this.registerChangeButtonListener();
    }

    private async validateVat(): Promise<void> {
        this.showLoadingIndication();

        if (!this.isVatIdFormatValid(this.vatIdInput.value)) {
            this.showFormatValidationWarning();
            this.hideLoadingIndication();
        } else {
            this.hideFormatValidationWarning();
        }

        if (Boolean(this.vatCountry.value)) {
            hide(this.countryNotPicked);
        } else {
            show(this.countryNotPicked);
            this.hideLoadingIndication();
        }

        let validationResult = await this.validateVatId(this.vatIdInput.value, this.vatCountry.value);
        this.setValidationStatus(validationResult);
        this.hideLoadingIndication();
    }

    private registerValidationButtonListener(): void {
        addEventListenerIfNotExists(this.validationButtonElement, 'click', async (event: Event) => {
            await this.validateVat();
        });

        addEventListenerIfNotExists(this.vatIdInput, 'keypress', async (event: KeyboardEvent) => {

            if (event.keyCode == EnterKeyCode) {
                await this.validateVat();
            }
            return;
        });
    }

    private registerChangeButtonListener(): void {
        addEventListenerIfNotExists(this.changeVatIdButtonElement, 'click', (event: Event) => {
            this.enableVatIdInput();
            this.enableValidationButton();
            this.disableChangeVatIdButton();
            this.enableHtmlElement(this.vatCountry);
        });
    }

    public async loadVatValidationAndCartSummary(): Promise<void> {
        await this.loadVatValidation();
        this.reloadCartSummary();
    }

    public async loadVatValidation(): Promise<void> {
        if (!this.deliveryRegionElement)
            return;

        let regionId = Number(this.deliveryRegionElement.dataset.activeRegion);
        if (!regionId)
            return;

        this.showLoadingIndication();
        await this.toggleVatValidationContainer(regionId);

        const client = new HttpClient();
        const url = `${getApiUrl()}/cart/validate-vat`
        const response = await client.fetch(url, {
            headers: {
                'Current-Language-Id': getCurrentLanguageId()
            }
        });
        this.hideLoadingIndication();

        if (!response.ok)
            return;

        let vatValidationResponse = await response.json() as VatValidationResponse;  
        if (!vatValidationResponse)
            return;

        if (!vatValidationResponse.isVatValidationEnabled) {
            this.enableContinueButton();
            this.hideBlockedVatWarning();
            return;
        }

        this.setValidationStatus(vatValidationResponse);

        if (vatValidationResponse.vatNumber)
            this.vatIdInput.setAttribute('value', vatValidationResponse.vatNumber);

        if (vatValidationResponse.countryIndex) {
            this.vatCountry.selectedIndex = vatValidationResponse.countryIndex;
            hide(this.countryNotPicked);
        }
    }

    private reloadCartSummary() {
        const action = new ActionCreator().reloadComponents(['CartSummary']);
        new AjaxDispatcher().dispatchGetAction(action);
    }

    private isVatIdFormatValid(VatIdString: string): boolean {
        if (!VatIdString || VatIdString.trim().length == 0)
            return false;

        return VatIdPattern.test(VatIdString);        
    } 

    private async validateVatId(vatIdString: string, countryCode: string): Promise<VatValidationResponse> {
        const client = new HttpClient();
        const url = `${getApiUrl()}/cart/validate-vat`;

        const response = await client.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json;',
                'Current-Language-Id': getCurrentLanguageId()
            },
            body: JSON.stringify({
                vatId: vatIdString,
                CountryCode: countryCode,
                CountryIndex: this.vatCountry.selectedIndex
            })
        });

        this.reloadCartSummary();

        if (response.ok)
            return await response.json() as VatValidationResponse;

        return null;
    }

    private setValidationStatus(vatValidationResponse: VatValidationResponse) {
        this.vatValidationStatusMessage.innerHTML = vatValidationResponse.validationStatusMessage;

        switch (vatValidationResponse.validationResultCombination) {
            case VatValidationResultCombination.UnvalidatedDefault:
                this.hideBlockedVatWarning();
                this.enableContinueButton();
                this.hideErrorMessage();
                this.enableVatInputs();
                this.useWarningMessageStyle();
                return;
            case VatValidationResultCombination.UnvalidatedDefaultBlockedContinue:
                this.showBlockedVatWarning();
                this.disableContinueButton();
                this.hideErrorMessage();
                this.enableVatInputs();
                this.useWarningMessageStyle();
                return;
            case VatValidationResultCombination.InvalidBlockedContinue:
                this.showBlockedVatWarning();
                this.disableContinueButton();
                this.hideErrorMessage();
                this.disableVatInputs();
                this.useWarningMessageStyle();
                return;
            case VatValidationResultCombination.Invalid:
                this.hideBlockedVatWarning();
                this.enableContinueButton();
                this.hideErrorMessage();
                this.disableVatInputs();
                this.useWarningMessageStyle();
                return;
            case VatValidationResultCombination.UnvalidatedError:
                this.hideBlockedVatWarning();
                this.showErrorMessage(vatValidationResponse.validationResultMessage);
                this.enableContinueButton();
                this.enableVatInputs();
                this.useWarningMessageStyle();
                return;
            case VatValidationResultCombination.UnvalidatedErrorBlockedContinue:
                this.showBlockedVatWarning();
                this.disableContinueButton();
                this.showErrorMessage(vatValidationResponse.validationResultMessage);
                this.enableVatInputs();
                this.useWarningMessageStyle();
                return;
            case VatValidationResultCombination.Valid:
                this.hideBlockedVatWarning();
                this.enableContinueButton();
                this.hideErrorMessage();
                this.disableVatInputs();
                this.useSuccessMessageStyle();
                return;
        }
    }

    private useWarningMessageStyle() {
        addClass(this.vatValidationStatusMessage, warningCssClass);
        removeClass(this.vatValidationStatusMessage, successCssClass);
    }

    private useSuccessMessageStyle() {
        addClass(this.vatValidationStatusMessage, successCssClass);
        removeClass(this.vatValidationStatusMessage, warningCssClass);
    }

    private disableVatInputs() {
        this.enableChangeVatIdButton();
        this.disableValidationButton();
        this.disableVatIdInput();
        this.disableHtmlElement(this.vatCountry);
    }

    private enableVatInputs() {
        this.disableChangeVatIdButton();
        this.enableValidationButton();
        this.enableVatIdInput();
        this.enableHtmlElement(this.vatCountry);
    }

    private async loadDeliveryRegions(): Promise<DeliveryRegion[]> {
        const client = new HttpClient();
        const url = `${getApiUrl()}/delivery-region`
        const response = await client.fetch(url, {
            headers: {
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (!response.ok) return;
        return await response.json() as DeliveryRegion[];
    }
    
    private flattenDeliveryRegions(regions: DeliveryRegion[]): DeliveryRegion[]{
        const flatRegions = new Array<DeliveryRegion>();
        regions.forEach(x => {
           if(x.hasChildren){
               const childRegions = this.flattenDeliveryRegions(x.children);
               childRegions.forEach(x => flatRegions.push(x));
           } 
           flatRegions.push(x);
        });
        
        return flatRegions;
    }

    private async toggleVatValidationContainer(regionId: number): Promise<void> {
        let regions = await this.loadDeliveryRegions();
        regions = this.flattenDeliveryRegions(regions);
        const region = regions.find(x => x.regionId === regionId);
        
        if(region != null && region.vatValidation){
            this.showVatValidationContainer();
        } else {
            this.hideVatValidationContainer();
        }
    }

    private showVatValidationContainer() {
        show(this.vatValidationContainer);
    }

    private hideVatValidationContainer() {
        hide(this.vatValidationContainer);
    }

    private showErrorMessage(errorMessage: string) {
        const errorMessageContainer = document.querySelector('#validationResult') as HTMLElement;
        const errorMessageLabel = document.querySelector('#errorMessage') as HTMLElement;
        show(errorMessageContainer);
        errorMessageLabel.textContent = errorMessage;
    }

    private hideErrorMessage() {
        const errorMessageContainer = document.querySelector('#validationResult') as HTMLElement;
        const errorMessageLabel = document.querySelector('#errorMessage') as HTMLElement;
        hide(errorMessageContainer);
        errorMessageLabel.innerHTML = "";
    }

    private showFormatValidationWarning(): void {
        show(this.validationWarningElement);
    }

    private hideFormatValidationWarning(): void {
        hide(this.validationWarningElement);
    }

    private showBlockedVatWarning(): void {
        show(this.vatValidationWarningContainer);
        this.disableContinueButton();
    }

    private hideBlockedVatWarning(): void {
        hide(this.vatValidationWarningContainer);
        this.enableContinueButton();
    }

    private enableValidationButton() {
        this.enableHtmlElement(this.validationButtonElement);
    }

    private disableValidationButton() {
        this.disableHtmlElement(this.validationButtonElement);
    }

    private enableVatIdInput() {
        this.enableHtmlElement(this.vatIdInput);
    }

    private disableVatIdInput() {
        this.disableHtmlElement(this.vatIdInput);
    }

    private enableChangeVatIdButton() {
        this.changeVatIdButtonElement.style.display = "initial";
        this.enableHtmlElement(this.changeVatIdButtonElement);
    }

    private disableChangeVatIdButton() {
        hide(this.changeVatIdButtonElement);
        this.disableHtmlElement(this.changeVatIdButtonElement);
    }

    private enableContinueButton() {
        const continueButton = document.querySelector('#deliveryPaymentContinueButton') as HTMLElement;
        this.enableHtmlElement(continueButton);
    }

    private disableContinueButton(): void {
        const continueButton = document.querySelector('#deliveryPaymentContinueButton') as HTMLElement;
        this.disableHtmlElement(continueButton);
    }

    private enableHtmlElement(element: HTMLElement): void {
        element.style.pointerEvents = "auto";
        removeClass(element, 'disabled');
        enable(element);
    }

    private disableHtmlElement(element: HTMLElement): void {
        element.style.pointerEvents = "none";
        addClass(element, 'disabled');
        disable(element);
    }

    private showLoadingIndication(): void {
        LoadingIndication.show(this.vatValidationBox);
    }

    private hideLoadingIndication(): void {
        LoadingIndication.hide(this.vatValidationBox);
    }
}

interface VatValidationResponse {
    isVatValidationEnabled: boolean;
    vatNumber: string,
    validationResultCombination: VatValidationResultCombination;
    validationStatusMessage: string;
    validationResultMessage: string;
    countryIndex: number;
}

const enum VatValidationResultCombination {
    UnvalidatedDefault = 0,
    UnvalidatedDefaultBlockedContinue = 1,
    UnvalidatedError = 2,
    UnvalidatedErrorBlockedContinue = 3,
    Invalid = 4,
    InvalidBlockedContinue = 5,
    Valid = 6
}
